import { normalizeExcludeTerms } from "./monitoringExcludeTermUtils";

export type SearchTermSource = "CONFIG" | "INLINE_CREATED";

export interface SearchTermRef {
  searchTermId?: string;
  term: string;
  source: SearchTermSource;
}

export interface SearchTermOption extends SearchTermRef {
  normalizedTerm: string;
  enabled: boolean;
  excludeTerms: string[];
}

export interface SearchTermExcludeDisplayRow extends SearchTermRef {
  excludeTerms: string[];
}

export const normalizeSearchTermText = (value: unknown) => String(value ?? "").trim().replace(/\s+/g, " ");

export const normalizeSearchTermKey = (value: unknown) => normalizeSearchTermText(value).toLocaleLowerCase("ko-KR");

const getSearchTermId = (item: any) => normalizeSearchTermText(item?.searchTermId || item?.keyword_id || item?.id);

const getSearchTermValue = (item: any) => normalizeSearchTermText(item?.term || item?.value || item?.name || item?.keyword);

const getSearchTermSource = (item: any, fallback: SearchTermSource): SearchTermSource =>
  item?.source === "INLINE_CREATED" ? "INLINE_CREATED" : fallback;

export const mapSearchTermOption = (item: any, fallbackSource: SearchTermSource = "CONFIG"): SearchTermOption | null => {
  const term = getSearchTermValue(item);
  if (!term) return null;
  return {
    searchTermId: getSearchTermId(item) || undefined,
    term,
    normalizedTerm: normalizeSearchTermKey(item?.normalizedTerm || term),
    enabled: item?.enabled !== false,
    source: getSearchTermSource(item, fallbackSource),
    excludeTerms: normalizeExcludeTerms(item?.excludeTerms),
  };
};

export const resolveActiveSearchTermDuplicate = (
  options: SearchTermOption[],
  input: string
): SearchTermOption | undefined => {
  const normalizedInput = normalizeSearchTermKey(input);
  if (!normalizedInput) return undefined;
  return options.find((option) => option.normalizedTerm === normalizedInput);
};

export const optionToSearchTermRef = (option: SearchTermOption): SearchTermRef => ({
  searchTermId: option.searchTermId,
  term: option.term,
  source: option.source,
});

export const refToSearchTermOption = (ref: SearchTermRef): SearchTermOption => ({
  searchTermId: ref.searchTermId,
  term: normalizeSearchTermText(ref.term),
  normalizedTerm: normalizeSearchTermKey(ref.term),
  enabled: true,
  source: ref.source,
  excludeTerms: [],
});

export const mergeSearchTermOptions = (...groups: Array<Array<SearchTermOption | null | undefined>>): SearchTermOption[] => {
  const byKey = new Map<string, SearchTermOption>();
  groups.flat().forEach((option) => {
    if (!option || !option.term) return;
    const normalizedOption = {
      ...option,
      term: normalizeSearchTermText(option.term),
      normalizedTerm: normalizeSearchTermKey(option.normalizedTerm || option.term),
      excludeTerms: normalizeExcludeTerms(option.excludeTerms),
    };
    const key = normalizedOption.normalizedTerm;
    const previous = byKey.get(key);
    byKey.set(
      key,
      previous
        ? {
            ...previous,
            ...normalizedOption,
            searchTermId: normalizedOption.searchTermId || previous.searchTermId,
            source: normalizedOption.source || previous.source,
            excludeTerms: normalizedOption.excludeTerms.length ? normalizedOption.excludeTerms : previous.excludeTerms,
          }
        : normalizedOption
    );
  });
  return Array.from(byKey.values()).sort((a, b) => a.term.localeCompare(b.term, "ko-KR"));
};

export const mergeSearchTermRefs = (refs: SearchTermRef[]): SearchTermRef[] => {
  const byKey = new Map<string, SearchTermRef>();
  refs.forEach((ref) => {
    const term = normalizeSearchTermText(ref.term);
    if (!term) return;
    const normalizedTerm = normalizeSearchTermKey(term);
    const key = normalizedTerm;
    const previous = byKey.get(key);
    byKey.set(key, {
      searchTermId: ref.searchTermId || previous?.searchTermId,
      term,
      source: ref.source || previous?.source || "CONFIG",
    });
  });
  return Array.from(byKey.values());
};

export const buildSearchTermRefsFromMonitoring = (item: any): SearchTermRef[] => {
  const refs = Array.isArray(item?.searchTermRefs)
    ? item.searchTermRefs
        .map((ref: any) => ({
          searchTermId: normalizeSearchTermText(ref.searchTermId || ref.keyword_id || ref.id) || undefined,
          term: normalizeSearchTermText(ref.term || ref.value || ref.name),
          source: getSearchTermSource(ref, "CONFIG"),
        }))
        .filter((ref: SearchTermRef) => Boolean(ref.term))
    : [];
  if (refs.length) return mergeSearchTermRefs(refs);

  const terms = Array.isArray(item?.searchTerms) ? item.searchTerms : Array.isArray(item?.includeTerms) ? item.includeTerms : [];
  const ids = Array.isArray(item?.searchTermIds) ? item.searchTermIds : [];
  return mergeSearchTermRefs(
    terms.map((term: string, index: number) => ({
      searchTermId: normalizeSearchTermText(ids[index]) || undefined,
      term: normalizeSearchTermText(term),
      source: "CONFIG",
    }))
  );
};

export const getSearchTermNames = (refs: SearchTermRef[]) => refs.map((ref) => ref.term);

export const getSearchTermIds = (refs: SearchTermRef[]) =>
  refs.map((ref) => ref.searchTermId).filter((id): id is string => Boolean(id));

export const resolveSearchTermExcludeRows = (
  refs: SearchTermRef[],
  options: SearchTermOption[]
): SearchTermExcludeDisplayRow[] => {
  const byId = new Map<string, SearchTermOption>();
  const byTerm = new Map<string, SearchTermOption>();
  options.forEach((option) => {
    if (option.searchTermId) byId.set(option.searchTermId, option);
    byTerm.set(normalizeSearchTermKey(option.term), option);
  });

  return mergeSearchTermRefs(refs).map((ref) => {
    const option = (ref.searchTermId ? byId.get(ref.searchTermId) : undefined) ?? byTerm.get(normalizeSearchTermKey(ref.term));
    return {
      searchTermId: ref.searchTermId || option?.searchTermId,
      term: ref.term || option?.term || "",
      source: ref.source,
      excludeTerms: option?.excludeTerms ?? [],
    };
  });
};
